import {
  fetch_requestor_application_audit_logs,
  fetch_reassignable_users,
} from '@/services/requestor_portal_service';
import { load_visible_open_company_cases } from '../CompanyCases/company_cases_loading';
import {
  ALL_TIME_RANGE_FILTER,
  ALL_STATUS_FILTER,
  EVENT_FILTERS,
  RANGE_FILTERS,
  audit_error_message,
  build_case_lookup,
  clone,
  entry_in_range,
  normalize_entries,
  read_session_project_id,
  to_number,
} from './audit_log_helpers';
import { audit_rows_to_pdf } from './audit_log_pdf_helpers';
import BlockingLoaderOverlay from '@/components/config_components/BlockingLoaderOverlay/BlockingLoaderOverlay.vue';

const AUDIT_LOG_BACKEND_PAGE_SIZE = 1000;

const AUDIT_LOG_STATE = {
  search_text: '',
  selected_actor: 'All users',
  selected_event: 'All events',
  selected_status: ALL_STATUS_FILTER,
  selected_range: ALL_TIME_RANGE_FILTER,
  current_page: 1,
  page_size: 10,
  event_filters: EVENT_FILTERS,
  range_filters: RANGE_FILTERS,
  entries: [],
  actor_people: [],
  case_lookup: {},
  loading_entries: false,
  entries_error: ''
};

function create_audit_log_state() {
  return clone(AUDIT_LOG_STATE);
}

function entry_matches_search(entry = {}, search = '') {
  const haystack = [
    entry.time,
    entry.actor,
    entry.status,
    entry.action,
    entry.action_label,
    entry.target,
    entry.diff,
  ].join(' ').toLowerCase();
  return !search || haystack.includes(search);
}

function export_file_stamp() {
  return new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
}

function csv_value(value = '') {
  let normalized = String(value ?? '').replace(/\r?\n/g, ' ').trim();
  if (/^[=+\-@]/.test(normalized)) {
    normalized = `'${normalized}`;
  }
  return `"${normalized.replace(/"/g, '""')}"`;
}

function normalize_text(value = '') {
  return String(value || '').trim();
}

function title_case(value = '') {
  return normalize_text(value)
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function normalize_email(value = '') {
  const normalized = normalize_text(value).toLowerCase();
  return normalized.includes('@') ? normalized : '';
}

function actor_identity_key(actor = '', role = '', email = '') {
  return [actor, role, email]
    .map((part) => normalize_text(part).toLowerCase())
    .filter(Boolean)
    .join('::');
}

function backend_person_id(person = {}) {
  return to_number(person?.user_id || person?.id || person?.user?.id);
}

function backend_person_name(person = {}) {
  const full_name = [
    person?.user?.first_name || person?.first_name,
    person?.user?.last_name || person?.last_name,
  ].map(normalize_text).filter(Boolean).join(' ');

  return title_case(
    full_name
    || person?.name
    || person?.full_name
    || person?.user?.email
    || person?.email
    || person?.user?.username
    || person?.username
  );
}

function backend_person_role(person = {}) {
  const role = person?.user?.role || person?.role || {};
  return title_case(
    (typeof role === 'string' ? role : '')
    || role?.role
    || role?.role_name
    || role?.name
    || person?.role_name
    || person?.user?.role_name
    || person?.user_type__name
  );
}

function backend_person_email(person = {}) {
  return normalize_email(
    person?.user?.email
    || person?.email
    || person?.user_email
    || person?.user?.username
    || person?.username
  );
}

function backend_actor_option(person = {}) {
  const user_id = backend_person_id(person);
  const actor = backend_person_name(person);
  if (!actor) return null;

  const role = backend_person_role(person);
  const email = backend_person_email(person);
  const identity_key = actor_identity_key(actor, role, email);

  return {
    value: user_id ? `user:${user_id}` : `actor:${identity_key}`,
    user_id,
    actor,
    role,
    email,
    actor_role_key: actor_identity_key(actor, role),
    identity_key,
    no_email_key: actor_identity_key(actor, role),
  };
}

function actor_option_matches_entry(option = {}, entry = {}) {
  if (!option?.value) return false;

  const entry_user_id = to_number(entry?.actor_key || entry?.raw?.user_id || entry?.raw?.reviewer_id);
  if (option.user_id && entry_user_id && option.user_id === entry_user_id) {
    return true;
  }

  if (option.email && entry.actor_email && option.email === entry.actor_email) {
    return true;
  }

  if (option.identity_key && option.identity_key === entry.actor_filter_key) {
    return true;
  }

  return option.no_email_key
    && option.no_email_key === actor_identity_key(entry.actor, entry.actor_role);
}

export default {
  name: 'AuditLog',
  components: {
    BlockingLoaderOverlay,
  },
  data: () => create_audit_log_state(),
  computed: {
    project_id() {
      return to_number(
        this.$route?.query?.project_id
        || this.$route?.query?.projectId
        || read_session_project_id()
      );
    },
    filtered_entries() {
      const search = this.search_text.trim().toLowerCase();
      const selected_actor = this.actor_filters.find((actor) => actor.value === this.selected_actor);
      return this.entries.filter((entry) => {
        const matches_actor = this.selected_actor === 'All users'
          || actor_option_matches_entry(selected_actor, entry);
        const matches_event = this.selected_event === 'All events'
          || entry.event_key === this.selected_event;
        const matches_status = this.selected_status === ALL_STATUS_FILTER
          || entry.status_key === String(this.selected_status || '').toLowerCase();
        const matches_range = entry_in_range(entry, this.selected_range);
        return matches_actor && matches_event && matches_status && matches_range && entry_matches_search(entry, search);
      });
    },
    actor_filters() {
      const seen = new Set();
      const backend_options = this.actor_people
        .map(backend_actor_option)
        .filter(Boolean);
      const actor_role_email_counts = backend_options.reduce((counts, option) => {
        if (option.email) {
          const emails = counts.get(option.actor_role_key) || new Set();
          emails.add(option.email);
          counts.set(option.actor_role_key, emails);
        }
        return counts;
      }, new Map());
      const options = [{ label: 'All users', value: 'All users' }];

      backend_options.forEach((option) => {
        const key = option.value;
        if (!key || seen.has(key)) {
          return;
        }

        seen.add(key);
        const role_label = option.role || 'No role';
        const should_show_email = (actor_role_email_counts.get(option.actor_role_key)?.size || 0) > 1;
        const qualifiers = [role_label, should_show_email ? option.email : ''].filter(Boolean);
        options.push({
          ...option,
          label: `${option.actor} (${qualifiers.join(', ')})`,
          value: key,
        });
      });

      return options;
    },
    status_filters() {
      const seen = new Set();
      const options = [ALL_STATUS_FILTER];

      this.entries.forEach((entry) => {
        const key = String(entry.status_key || '').toLowerCase();
        if (!key || seen.has(key)) {
          return;
        }

        seen.add(key);
        options.push(entry.status);
      });

      return options;
    },
    total_pages() {
      return Math.max(1, Math.ceil(this.filtered_entries.length / Number(this.page_size || 10)));
    },
    paginated_entries() {
      const page_size = Number(this.page_size || 10);
      const start = (this.current_page - 1) * page_size;
      return this.filtered_entries.slice(start, start + page_size);
    },
    pagination_start() {
      if (!this.filtered_entries.length) return 0;
      return ((this.current_page - 1) * Number(this.page_size || 10)) + 1;
    },
    pagination_end() {
      return Math.min(this.current_page * Number(this.page_size || 10), this.filtered_entries.length);
    },
    pagination_summary() {
      if (!this.filtered_entries.length) return 'Showing 0 of 0 system audit events';
      return `Showing ${this.pagination_start.toLocaleString('en-ZA')} to ${this.pagination_end.toLocaleString('en-ZA')} of ${this.filtered_entries.length.toLocaleString('en-ZA')} system audit events`;
    },
    visible_page_numbers() {
      const pages = [];
      const start = Math.max(1, this.current_page - 2);
      const end = Math.min(this.total_pages, start + 4);

      for (let page = start; page <= end; page += 1) {
        pages.push(page);
      }

      return pages;
    },
    can_go_previous() {
      return this.current_page > 1;
    },
    can_go_next() {
      return this.current_page < this.total_pages;
    },
    footer_summary() {
      if (this.entries_error) return 'Audit events unavailable';
      return `${this.filtered_entries.length.toLocaleString('en-ZA')} filtered from ${this.entries.length.toLocaleString('en-ZA')} system audit events`;
    },
    latest_seal_summary() {
      const latest_entry = this.entries[0];
      return latest_entry?.time
        ? `Latest event ${latest_entry.time}`
        : 'No backend events returned';
    },
    page_loading_message() {
      return 'Loading audit log...';
    },
    page_loading_detail() {
      return 'Fetching backend audit events for this project.';
    },
    current_role() {
      return String(this.$store?.getters?.currentRole || '');
    },
    can_route_cases() {
      return Boolean(this.$store?.getters?.canRouteCases);
    }
  },
  created() {
    this.load_audit_entries();
  },
  watch: {
    search_text() {
      this.reset_pagination();
    },
    selected_actor() {
      this.reset_pagination();
    },
    selected_event() {
      this.reset_pagination();
    },
    selected_status() {
      this.reset_pagination();
    },
    selected_range() {
      this.reset_pagination();
    },
    page_size() {
      this.reset_pagination();
    },
    filtered_entries() {
      if (this.current_page > this.total_pages) {
        this.current_page = this.total_pages;
      }
    },
  },
  methods: {
    async fetch_case_lookup() {
      const cases = await load_visible_open_company_cases({
        project_id: this.project_id,
        current_role: this.current_role,
        can_route_cases: this.can_route_cases,
      });
      return build_case_lookup(cases);
    },
    fetch_audit_entries() {
      const params = {
        page: 1,
        page_size: AUDIT_LOG_BACKEND_PAGE_SIZE,
        ...(this.project_id ? { project_id: this.project_id } : {}),
      };
      return fetch_requestor_application_audit_logs(params);
    },
    fetch_actor_people() {
      const params = this.project_id ? { project_id: this.project_id } : {};
      return fetch_reassignable_users(params);
    },
    update_audit_state(entries = [], case_lookup = new Map(), actor_people = [], error = null) {
      this.entries = error ? [] : normalize_entries(entries, case_lookup);
      this.actor_people = error ? [] : actor_people;
      this.case_lookup = case_lookup;
      this.entries_error = error ? audit_error_message(error) : '';
    },
    async load_audit_entries() {
      this.loading_entries = true;
      this.entries_error = '';
      try {
        const [entries, case_lookup, actor_people] = await Promise.all([
          this.fetch_audit_entries(),
          this.fetch_case_lookup(),
          this.fetch_actor_people(),
        ]);
        this.update_audit_state(entries, case_lookup, actor_people);
      } catch (error) {
        this.update_audit_state([], new Map(), [], error);
      } finally {
        this.loading_entries = false;
      }
    },
    reset_pagination() {
      this.current_page = 1;
    },
    set_page(page) {
      const normalized_page = Number(page);
      if (!Number.isFinite(normalized_page)) return;
      this.current_page = Math.min(Math.max(1, normalized_page), this.total_pages);
    },
    previous_page() {
      if (this.can_go_previous) {
        this.set_page(this.current_page - 1);
      }
    },
    next_page() {
      if (this.can_go_next) {
        this.set_page(this.current_page + 1);
      }
    },
    export_rows() {
      return this.filtered_entries.map((entry) => ({
        time: entry.time,
        actor: entry.actor,
        actor_role: entry.actor_role || '',
        status: entry.status || 'No status',
        source: entry.source || '',
        event: entry.event_key,
        action: entry.action_label,
        target: entry.target,
        details: entry.diff || '-',
      }));
    },
    export_excel() {
      const rows = this.export_rows();
      if (!rows.length) return;

      const headers = ['Time', 'Actor', 'Actor role', 'Status', 'Source', 'Event', 'Action', 'Case / Target', 'Details'];
      const body = rows.map((row) => [
        row.time,
        row.actor,
        row.actor_role,
        row.status,
        row.source,
        row.event,
        row.action,
        row.target,
        row.details,
      ].map(csv_value).join(','));
      const csv = `\uFEFFsep=,\r\n${[headers.map(csv_value).join(','), ...body].join('\r\n')}`;
      this.download_text_file(`audit-log-${export_file_stamp()}.csv`, csv, 'text/csv;charset=utf-8;');
    },
    export_pdf() {
      const rows = this.export_rows();
      if (!rows.length) return;

      const pdf = audit_rows_to_pdf(rows, {
        generated_at: new Date().toLocaleString('en-ZA'),
        row_count: rows.length,
      });
      this.download_text_file(`audit-log-${export_file_stamp()}.pdf`, pdf, 'application/pdf');
    },
    download_text_file(filename, content, type) {
      if (typeof document === 'undefined') return;

      const blob = new Blob([content], { type });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
};
